import { useState } from 'react'
import { LogOut, Building2, Briefcase, ChevronRight, Upload, Users, Trash2, AlertTriangle, MessageCircle } from 'lucide-react'
import OrganizationsTab from '../components/organizations/OrganizationsTab'
import BranchesTab from '../components/branches/BranchesTab'
import BulkUploadModal from '../components/upload/BulkUploadModal'
import UsersPanel from '../components/users/UsersPanel'
import SupportPanel from '../components/support/SupportPanel'
import { canContactSupport as canContact } from '../hooks/useSupport'

const ROLE_LABELS = { superadmin: 'Super Admin', admin: 'Admin', manager: 'Manager', staff: 'Staff' }
const ROLE_COLORS = {
  superadmin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  manager: 'bg-green-100 text-green-700',
  staff: 'bg-gray-100 text-gray-700',
}

function getInitials(name) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
}

export default function ProfilePage({
  currentUser,
  onLogout,
  onDeleteAccount,
  organizations,
  branches,
  onAdd,
  onUpdate,
  onDelete,
  onManagePlan,
  onAddBranch,
  onUpdateBranch,
  onDeleteBranch,
  onAddEntry,
  onImported,
  users,
  onAddUser,
  onAssignUser,
  onDeactivateUser,
  onReactivateUser,
  onResetPassword,
  onDeleteUser,
  initialAdminTab = null,
  support,
}) {
  const [adminTab, setAdminTab] = useState(initialAdminTab)
  const [showUpload, setShowUpload] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Support tickets: org members contact the PetroTrack team from here. The
  // hook lives in AppShell so this page and the floating widget read the same
  // tickets and the same unread count.
  const canContactSupport = canContact(currentUser)

  async function handleDeleteAccount() {
    setDeleting(true)
    setDeleteError('')
    const result = await onDeleteAccount?.()
    setDeleting(false)
    if (result && !result.success) {
      setDeleteError(result.message || 'Failed to delete account.')
      return
    }
    // On success the session is signed out; the auth listener returns to login.
  }
  const isSuperAdmin = currentUser.role === 'superadmin'
  const isAdmin = currentUser.role === 'admin'

  if (adminTab === 'organizations') {
    return (
      <div className="animate-fadeIn space-y-4">
        <button
          onClick={() => setAdminTab(null)}
          className="flex items-center space-x-1.5 text-blue-600 text-sm font-medium"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span>Back to Profile</span>
        </button>
        <OrganizationsTab
          organizations={organizations}
          branches={branches}
          onAdd={onAdd}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onManagePlan={onManagePlan}
        />
      </div>
    )
  }

  if (adminTab === 'branches') {
    return (
      <div className="animate-fadeIn space-y-4">
        <button
          onClick={() => setAdminTab(null)}
          className="flex items-center space-x-1.5 text-blue-600 text-sm font-medium"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span>Back to Profile</span>
        </button>
        <BranchesTab
          organizations={organizations}
          branches={branches}
          onAdd={onAddBranch}
          onUpdate={onUpdateBranch}
          onDelete={onDeleteBranch}
        />
      </div>
    )
  }

  if (adminTab === 'support') {
    return (
      <div className="animate-fadeIn space-y-4">
        <button
          onClick={() => setAdminTab(null)}
          className="flex items-center space-x-1.5 text-blue-600 text-sm font-medium"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span>Back to Profile</span>
        </button>
        <SupportPanel currentUser={currentUser} support={support} />
      </div>
    )
  }

  if (adminTab === 'users') {
    return (
      <div className="animate-fadeIn space-y-4">
        <button
          onClick={() => setAdminTab(null)}
          className="flex items-center space-x-1.5 text-blue-600 text-sm font-medium"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span>Back to Profile</span>
        </button>
        <UsersPanel
          currentUser={currentUser}
          users={users}
          organizations={organizations}
          branches={branches}
          onAddUser={onAddUser}
          onAssignUser={onAssignUser}
          onDeactivateUser={onDeactivateUser}
          onReactivateUser={onReactivateUser}
          onResetPassword={onResetPassword}
          onDeleteUser={onDeleteUser}
        />
      </div>
    )
  }

  const userOrg = organizations.find((o) => o.id === currentUser.organizationId)
  const userBranch = branches.find((b) => b.id === currentUser.branchId)

  return (
    <div className="animate-fadeIn space-y-4">
      {/* User card */}
      <div className="bg-white rounded-2xl shadow p-5">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {getInitials(currentUser.name)}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-800 truncate">{currentUser.name}</h2>
            <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[currentUser.role]}`}>
              {ROLE_LABELS[currentUser.role]}
            </span>
            {userOrg && <p className="text-xs text-gray-400 mt-1 truncate">{userOrg.name}</p>}
            {userBranch && <p className="text-xs text-gray-400 truncate">{userBranch.name}</p>}
          </div>
        </div>
      </div>

      {/* Management section — superadmin + admin */}
      {(isSuperAdmin || isAdmin) && (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <p className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Management
          </p>

          <button
            onClick={() => setAdminTab('users')}
            className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
          >
            <Users className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-800">{isSuperAdmin ? 'Users' : 'My Team'}</p>
              <p className="text-xs text-gray-400">
                {isSuperAdmin
                  ? `${users.filter(u => u.id !== currentUser.id).length} accounts`
                  : `${users.filter(u => u.organizationId === currentUser.organizationId && u.id !== currentUser.id).length} members`
                }
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>

          {isSuperAdmin && (
            <>
          <button
            onClick={() => setAdminTab('organizations')}
            className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
          >
            <Building2 className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-800">Organizations</p>
              <p className="text-xs text-gray-400">
                {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>

          <button
            onClick={() => setAdminTab('branches')}
            className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
          >
            <Briefcase className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-800">Branches</p>
              <p className="text-xs text-gray-400">
                {branches.length} branch{branches.length !== 1 ? 'es' : ''}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>

          <button
            onClick={() => setShowUpload(true)}
            className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
          >
            <Upload className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-800">Bulk Upload</p>
              <p className="text-xs text-gray-400">Import daily entries from CSV</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>
            </>
          )}
        </div>
      )}

      {showUpload && (
        <BulkUploadModal
          branches={branches}
          onAddEntry={onAddEntry}
          onImported={onImported}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Help */}
      {canContactSupport && (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <p className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Help</p>
          <button
            onClick={() => setAdminTab('support')}
            className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
          >
            <MessageCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-800">Contact support</p>
              <p className="text-xs text-gray-400">
                {support.tickets.length > 0
                  ? `${support.tickets.length} ticket${support.tickets.length !== 1 ? 's' : ''}`
                  : 'Questions, issues, feedback'}
              </p>
            </div>
            {support.unreadCount > 0 && (
              <span className="mr-2 min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-bold grid place-items-center">
                {support.unreadCount}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-gray-300" />
          </button>
        </div>
      )}

      {/* Sign out */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <p className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Account</p>
        <button
          onClick={onLogout}
          className="w-full flex items-center px-4 py-3.5 hover:bg-red-50 active:bg-red-100 transition border-t border-gray-100"
        >
          <LogOut className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
          <span className="text-sm font-semibold text-red-500">Sign Out</span>
        </button>
        <button
          onClick={() => { setDeleteError(''); setShowDeleteConfirm(true) }}
          className="w-full flex items-center px-4 py-3.5 hover:bg-gray-50 active:bg-gray-100 transition border-t border-gray-100"
        >
          <Trash2 className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-600">Delete my account</span>
        </button>
      </div>

      {/* Delete-account double-confirm (Decision 9) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !deleting && setShowDeleteConfirm(false)} />
          <div className="relative bg-white w-full sm:w-[420px] rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl"
               style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-bold text-gray-800 text-lg">Delete your account?</h3>
            <p className="text-sm text-gray-500 mt-1.5">
              This permanently deletes your login and profile. This cannot be undone. You will be signed out immediately.
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mt-3">{deleteError}</p>
            )}
            <div className="flex space-x-3 mt-5">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition"
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
